import useForm from '../../hooks/useForm';
import useAuth from '../../hooks/useAuth';
import User from '../../hooks/User';
import { UserContext } from '../../hooks/UserContext';
import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import UserDatabase from './TableViews/UserDatabase';
import UserAnalytics from './TableViews/Analytics';
import "./AdminDashboard.css"

// Modal component for action success/failure message
const SuccessModal = ({ success, message, onClose }) => {
  return (
    <div className="dashboard-modal-overlay">
      <div className="dashboard-modal-content">
        <h3>{success ? `Success` : `Failure`}</h3>
        <p>{message}</p>
        <button onClick={onClose} className="dashboard-close-modal-button">Close</button>
      </div>
    </div>
  );
};

export default function AdminDashboard(props) {
  const [tableView, setTableView] = useState("db"); // main table view, 'db' for User Database, 'analytics' for Analytics
  const [users, setUsers] = useState([]); // state to hold all user profile objects
  // array of {name:"", news_organization:"", user__email:"", user__id:"", user__is_staff:bool, user__is_active:bool, user__last_login:Date/null}
  const [loading, setLoading] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(null); // null, base, access
  const [actionModal, setActionModal] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [logfile, setLogfile] = useState("base"); // base, access
  const [logs, setLogs] = useState(null);
  const { values, handleChange, reset } = useForm({
    initialValues: {
      email: '',
      name: '',
      org: '',
    }
  });
  const [sortColumn, setSortColumn] = useState("user__email");
  const [sortDirection, setSortDirection] = useState("asc");

  const { registerUser, check_superuser } = useAuth();
  const { fetchUsers } = User();
  const { user } = useContext(UserContext);

  const logTypes = ["base", "access"];

  const getLogs = async (logfile = "base", lines = 500) => {
    let axiosT = axios.create({
      baseURL: `${process.env.REACT_APP_FEATURED_API}`,
      headers: {
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
    });
    return axiosT
      .get("logs/", {
        params: { logfile: logfile, lines: lines }
      })
      .then(async (res) => {
        return res.data.logs;
      })
      .catch((err) => {
        console.error("failed to fetch server logs", err);
      });
  };

  const handleRefreshLogs = async (logfile = "base", lines = 500) => {
    setLoadingLogs(logfile);
    setLogfile(logfile);

    try {
      const result = await getLogs(logfile, lines); // fetch latest 'lines' log lines (def. 500)
      setLogs(result);
    } catch (error) {
      console.error("Failed to fetch server logs", error);
    } finally {
      setLoadingLogs(null);
    }
  }

  const handleRegister = async (e) => {
    e.preventDefault();
    if (validateEmail()) {
      setLoading(true);
      setActionModal(false);
      setActionSuccess(false);
      setMessage("");

      try {
        const res = await registerUser(values);
        if (res.success) {
          const newUser = res.data;
          const newUserWithName = {
            name: values.name,
            news_organization: values.org,
            user__email: newUser.email,
            user__id: newUser.id,
            user__is_staff: newUser.is_staff,
            user__is_active: newUser.is_active,
            user__last_login: null
          };
          const updatedUsers = [...users, newUserWithName];
          setSortColumn("user__email");
          setSortDirection("asc");
          setUsers(updatedUsers);
          setActionSuccess(true);
          setMessage("User successfully registered!");
        } else {
          setActionSuccess(false);
          setMessage(`Error registering user: ${res.data}`);
        }
      } catch (err) {
        console.error(err);
        setActionSuccess(false);
        setMessage("Error registering user.");
      } finally {
        reset();
        setLoading(false);
        setActionModal(true);
      }

    }
  }

  const validateEmail = () => {
    let isValid = true;
    let input = values;
    document.getElementById('form-error').innerHTML = "";
    document.getElementById('message-email').innerHTML = "";

    if (!input["email"]) {
      isValid = false;
      document.getElementById('message-email').innerHTML = "Please enter valid email Address.";
    }
    if (input["email"]) {

      var pattern = new RegExp(/^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i);
      if (!pattern.test(input["email"])) {
        isValid = false;
        document.getElementById('message-email').innerHTML = "Please enter valid email address.";
      }
    }
    return isValid;
  }

  const handleSwitchTableView = (view) => {
    setTableView(view);
  }

  const renderUserTable = () => {
    switch (tableView) {
      case "db":
        return <UserDatabase
          isSuperuser={isSuperuser}
          users={users}
          setUsers={setUsers}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          setMessage={setMessage}
          setActionModal={setActionModal}
          setActionSuccess={setActionSuccess}
          handleSwitchTableView={handleSwitchTableView} />
      case "analytics":
        return <UserAnalytics
          handleSwitchTableView={handleSwitchTableView}
          users={users}
          getLogs={getLogs}
          setMessage={setMessage}
          setActionModal={setActionModal}
          setActionSuccess={setActionSuccess} />
      default:
        return <UserDatabase
          isSuperuser={isSuperuser}
          users={users}
          setUsers={setUsers}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          setMessage={setMessage}
          setActionModal={setActionModal}
          setActionSuccess={setActionSuccess}
          handleSwitchUserTable={handleSwitchTableView} />
    }
  }

  // fetch all database profiles on initial render, populate state
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchUsers();
        setUsers(data);
      } catch (error) {
        console.error("failed to fetch users");
      }
    })();
  }, [])

  useEffect(() => {
    const fetchSuperuserStatus = async () => {
      if (user) {
        const res = await check_superuser();
        setIsSuperuser(res);
      }
    };

    fetchSuperuserStatus();
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getLogs("base", 500);
        setLogs(data);
      } catch (error) {
        console.error("failed to fetch server logs");
      }
    })();
  }, []);

  return (
    <div style={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-start",
      height: "85vh"
    }}>
      <div className="dashboard-left-col">
        <div className="dashboard-register-container">
          <h3>Register a new user</h3>
          <form className="dashboard-form" onSubmit={handleRegister}>
            <div className="dashboard-message" id="form-error">
              {/* {error && <Error error={error}/>} */}
            </div>
            <div className="dashboard-form-group-container">
              <div className="dashboard-form-group">
                <label className="dashboard-label">{'Email '}{<span aria-hidden="true">*</span>}</label>
                <input
                  placeholder={"Email"}
                  name="email"
                  type="text"
                  className="dashboard-form-control"
                  value={values.email}
                  required
                  onChange={handleChange} />
                <label id="message-email" className="dashboard-message"></label>
              </div>
              <div className="dashboard-form-group">
                <label className="dashboard-label">Name</label>
                <input
                  placeholder={"Name"}
                  name="name"
                  type="text"
                  className="dashboard-form-control"
                  value={values.name}
                  onChange={handleChange} />
              </div>
            </div>
            <div className="dashboard-form-group">
              <label className="dashboard-label">Org</label>
              <input
                placeholder={"Name of news organization"}
                name="org"
                type="text"
                className="dashboard-form-control"
                value={values.org}
                onChange={handleChange} />
            </div>
            <button className="dashboard-register-button" type="submit" disabled={loading}>
              {loading ? (
                <div className="dashboard-spinner"></div>
              ) : ('Register')}
            </button>
          </form>
        </div>
        <div className="dashboard-logs-container">
          {logs ? logs : "no data"}
        </div>
        <div className="dashboard-log-tab-selector">
          {logTypes.map((logType) => (
            <button
              key={logType}
              className={`dashboard-logs-refresh-button ${logfile === logType ? "active" : ""
                }`}
              onClick={() => handleRefreshLogs(logType, 500)}
              disabled={loadingLogs === logType}
            >
              {loadingLogs === logType ? (
                <div className="dashboard-spinner"></div>
              ) : (`${logType} logs`)}
            </button>
          ))}
        </div>
      </div>
      {actionModal && <SuccessModal success={actionSuccess} message={message} onClose={() => setActionModal(false)} />}
      {renderUserTable()}
    </div>
  );
}
